import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, authReady, getAuthUserId } from '../services/supabase';
import { clearAllData } from '../services/storage/userDataStorage';
import { readProfileTier } from '../services/auth/profileTier';
import {
  isProfileWorld,
  getStoredTier,
  reconcileTierWithRow,
  isMergedAccountStatus,
  TIER_FULL,
} from '../utils/userTier';
import { getProfileUserId } from '../utils/identityUtils';
import { logEvent } from '../services/analytics/eventLogger';

/**
 * Two-part session handoff detection:
 *
 * 1. QR scan guard — `converted_profile_id` marker exists in localStorage.
 *    Synchronous check, no DB query. Shows takeover with "clear" escape hatch.
 *
 * 2. Live conversion check — Anon session was just converted in the PWA.
 *    Detects via `check_anon_converted` RPC on mount + visibilitychange.
 *    Clears old data, writes `converted_profile_id` marker.
 *
 * 3. Simple profile converted elsewhere (f/tier-integrity) — this container
 *    holds a Simple profile (stored tier copy 'simple'), and the database now
 *    says Full under another session's ownership: the person converted in the
 *    installed app. Same clear + marker + takeover as part 2 — never the
 *    phone-code login, which would kick the new app via the one-device rule.
 *    The rule is reconcileTier's (userTier.js); ensureProfileSynced skips its
 *    mismatch → OTP redirect in exactly this case.
 *
 *    Simple profile merged into the account on its number (f/simple-merge,
 *    039): the database row reads 'merged' — the same takeover (decision 1).
 *    Checked with or without a stored copy; a Full install never reads (only
 *    Simple rows merge).
 *
 * Returns { converted, variant, clearMarker }.
 */
export const useConversionCheck = () => {
  // Let /clear through — user is explicitly resetting
  const isClearPage = window.location.pathname === '/clear';
  const marker = localStorage.getItem('converted_profile_id');
  const [converted, setConverted] = useState(isClearPage ? false : !!marker);
  const [variant, setVariant] = useState(isClearPage ? null : marker ? 'qr-guard' : null);
  const convertedRef = useRef(!!marker);

  // Fire analytics for QR guard on mount
  useEffect(() => {
    if (marker) {
      logEvent('session_handoff_qr_guard');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear marker and reload — escape hatch for deleted accounts
  const clearMarker = useCallback(() => {
    localStorage.removeItem('converted_profile_id');
    window.location.reload();
  }, []);

  // Shared landing for both live checks: fire analytics, clear, mark, show.
  const takeOver = useCallback(async (profileUserId, path) => {
    convertedRef.current = true;
    logEvent('session_handoff_detected', path ? { path } : {});

    await clearAllData();

    // Write survival marker after clear (for future QR scan guard)
    localStorage.setItem('converted_profile_id', profileUserId);

    setVariant('conversion');
    setConverted(true);
  }, []);

  const checkConversion = useCallback(async () => {
    // Circuit breaker — already detected
    if (convertedRef.current) return;

    // A profile container never takes the guest relay below — a Simple mint
    // claims its own anon id, so the relay would wipe the minting tab
    // (device-caught 2026-08-08). Its only takeover is part 3: converted on
    // another device (needs a stored 'simple' copy — reconcileTier), or merged
    // into the account (any copy but Full).
    if (isProfileWorld()) {
      if (getStoredTier() === TIER_FULL) return;
      const profileUserId = getProfileUserId();
      try {
        await authReady;
        // Tier + owner through profile_tier_check (open-access lockdown): a
        // row converted on another device belongs to another session and
        // can't be read directly — the case this check exists for.
        const { row: data } = await readProfileTier(profileUserId);
        if (!data || convertedRef.current) return;
        if (reconcileTierWithRow(data, getAuthUserId()) === 'takeover') {
          const path = isMergedAccountStatus(data.account_status)
            ? 'simple_merged'
            : 'simple_converted_elsewhere';
          await takeOver(profileUserId, path);
        }
      } catch (error) {
        console.error('[CONVERSION_CHECK] Simple tier read failed:', error);
      }
      return;
    }

    // Live conversion check — active Anon session being converted
    const anonUserId = localStorage.getItem('anon_user_id');
    if (!anonUserId) return;

    try {
      const { data: profileUserId } = await supabase.rpc('check_anon_converted', {
        p_anon_user_id: anonUserId,
      });

      if (!profileUserId) return;

      await takeOver(profileUserId);
    } catch (error) {
      console.error('[CONVERSION_CHECK] RPC failed:', error);
    }
  }, [takeOver]);

  useEffect(() => {
    // Mount-time check (skip if QR guard already triggered or on /clear page)
    if (!marker && !isClearPage) {
      checkConversion();
    }

    // Visibility check — when user returns to this tab
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkConversion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [checkConversion, marker, isClearPage]);

  return { converted, variant, clearMarker };
};
